'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Upload, X, FileText, Loader2, Trash2, CheckCircle, ExternalLink } from 'lucide-react'
import { useEducationAdmin, EduKnowledge } from '@/hooks/useEducationAdmin'

interface KnowledgeUploaderProps {
    classId: string
    onUpdate?: () => void
}

const EDU = {
    blue: '#0ea5e9',
    green: '#10b981',
    white: '#0e0e17',
    border: 'rgba(255,255,255,0.08)',
    muted: 'rgba(255,255,255,0.55)',
    navy: '#ffffff',
    cream: 'rgba(255,255,255,0.03)',
    slate: 'rgba(255,255,255,0.7)',
}

export default function KnowledgeUploader({ classId, onUpdate }: KnowledgeUploaderProps) {
    const { t } = useTranslation()
    const { fetchKnowledge, uploadKnowledge, deleteKnowledge, addKnowledgeText } = useEducationAdmin()
    const [files, setFiles] = useState<EduKnowledge[]>([])
    const [loading, setLoading] = useState(true)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [pendingDelete, setPendingDelete] = useState<EduKnowledge | null>(null)
    const [pasteTitle, setPasteTitle] = useState('')
    const [pasteText, setPasteText] = useState('')
    const [pasteBusy, setPasteBusy] = useState(false)
    const [pasteError, setPasteError] = useState<string | null>(null)
    const [showPasteModal, setShowPasteModal] = useState(false)

    const loadFiles = useCallback(async () => {
        setLoading(true)
        try {
            const data = await fetchKnowledge(classId)
            setFiles(data)
        } catch (err) {
            console.error('Error loading knowledge:', err)
            setError(t('education.knowledgeUploader.errors.loadFailed'))
        } finally {
            setLoading(false)
        }
    }, [classId, fetchKnowledge, t])

    useEffect(() => {
        loadFiles()
    }, [loadFiles])

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const allowedTypes = ['application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
        if (!allowedTypes.includes(file.type) && !file.name.endsWith('.docx')) {
            setError(t('education.knowledgeUploader.errors.unsupportedType'))
            return
        }

        setUploading(true)
        setError(null)
        try {
            await uploadKnowledge(classId, file)
            await loadFiles()
            onUpdate?.()
        } catch (err) {
            console.error('Upload error:', err)
            setError(t('education.knowledgeUploader.errors.uploadFailed'))
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    const handleDelete = async (file: EduKnowledge) => {
        // Use inline confirm state instead of window.confirm (which blocks the
        // UI thread and doesn't work in mobile WebViews / PWAs)
        setPendingDelete(file)
    }

    const confirmDelete = async () => {
        if (!pendingDelete) return
        const file = pendingDelete
        setPendingDelete(null)
        try {
            await deleteKnowledge(file.id, file.file_url)
            setFiles(prev => prev.filter(f => f.id !== file.id))
            onUpdate?.()
        } catch (err) {
            console.error('Delete error:', err)
            setError(t('education.knowledgeUploader.errors.deleteFailed'))
        }
    }

    const handleAddPaste = async () => {
        if (!pasteText.trim() || pasteBusy) return
        setPasteBusy(true); setPasteError(null)
        try {
            await addKnowledgeText(classId, pasteTitle, pasteText)
            setPasteTitle(''); setPasteText('')
            await loadFiles()
            onUpdate?.()
        } catch (err) {
            setPasteError((err as Error).message || 'Failed to add document.')
        } finally {
            setPasteBusy(false)
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', boxSizing: 'border-box' }}>
            {/* Upload Area */}
            <div style={{
                position: 'relative',
                border: `2px dashed ${uploading ? EDU.blue : EDU.border}`,
                borderRadius: 20, padding: 'clamp(20px,4vw,32px) clamp(12px,3vw,24px)', textAlign: 'center',
                background: uploading ? `${EDU.blue}05` : EDU.white,
                transition: 'all 0.2s', boxSizing: 'border-box', width: '100%',
            }}>
                <input
                    id="edu-knowledge-file-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={uploading}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: uploading ? 'default' : 'pointer', width: '100%' }}
                    accept=".pdf,.txt,.docx"
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    {uploading ? (
                        <Loader2 size={32} className="animate-spin" color={EDU.blue} />
                    ) : (
                        <div style={{
                            width: 56, height: 56, borderRadius: 16, background: `${EDU.blue}12`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: EDU.blue,
                        }}>
                            <Upload size={24} />
                        </div>
                    )}
                    <div>
                        <p style={{ fontWeight: 700, color: EDU.navy, margin: '0 0 4px' }}>
                            {uploading ? t('education.knowledgeUploader.uploading') : t('education.knowledgeUploader.dropPrompt')}
                        </p>
                        <p style={{ fontSize: 13, color: EDU.muted, margin: 0 }}>
                            {t('education.knowledgeUploader.fileTypes')}
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, color: '#f87171', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <X size={14} /> {error}
                </motion.div>
            )}

                        {/* Paste-path knowledge — PDF/Word users: open the doc, select all, copy */}
            <div style={{ padding: 16, background: EDU.cream, borderRadius: 16, border: `1px solid ${EDU.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontWeight: 700, color: EDU.navy, margin: 0, fontSize: 13 }}>
                    …or paste document text
                </p>
                <input
                    type="text" value={pasteTitle} onChange={e => setPasteTitle(e.target.value)}
                    placeholder="Document title (e.g. Espresso preparation SOP)"
                    style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: 'rgba(255,255,255,0.04)', fontSize: 13, color: EDU.navy, outline: 'none', width: '100%', boxSizing: 'border-box' }}
                />
                <textarea
                    value={pasteText} onChange={e => setPasteText(e.target.value)} rows={4}
                    placeholder="Open your Word/PDF document, select all, copy, paste here — the AI reads this text directly"
                    style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: 'rgba(255,255,255,0.04)', fontSize: 12.5, color: EDU.navy, outline: 'none', resize: 'vertical', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
                />
                {pasteError && <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{pasteError}</p>}
                <button
                    onClick={handleAddPaste} disabled={pasteBusy || !pasteText.trim()}
                    style={{ alignSelf: 'flex-start', padding: '9px 18px', borderRadius: 10, border: 'none', background: pasteText.trim() ? EDU.blue : 'rgba(255,255,255,0.12)', color: '#ffffff', fontWeight: 700, fontSize: 12.5, cursor: pasteText.trim() ? 'pointer' : 'not-allowed' }}
                >
                    {pasteBusy ? 'Adding…' : 'Add document'}
                </button>
            </div>


            {/* Files List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: EDU.navy, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileText size={16} />
                    {t('education.knowledgeUploader.heading', { count: files.length })}
                </h4>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                        <Loader2 className="animate-spin" color={EDU.muted} />
                    </div>
                ) : files.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', background: EDU.cream, borderRadius: 16, border: `1px solid ${EDU.border}` }}>
                        <p style={{ color: EDU.muted, fontSize: 13, margin: 0 }}>
                            {t('education.knowledgeUploader.empty')}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {files.map(f => (
                            <motion.div key={f.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                style={{
                                    background: EDU.white, borderRadius: 14, padding: '12px 16px',
                                    border: `1px solid ${EDU.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                    <div style={{ color: EDU.blue, flexShrink: 0 }}><FileText size={20} /></div>
                                    <div style={{ minWidth: 0 }}>
                                        <p style={{ fontWeight: 600, color: EDU.navy, margin: 0, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {f.file_name}
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <p style={{ fontSize: 11, color: EDU.muted, margin: 0 }}>
                                                {t('education.knowledgeUploader.addedDate', {
                                                    date: new Date(f.created_at ?? Date.now()).toLocaleDateString(),
                                                })}
                                            </p>
                                            {(f as any).extracted_text ? (
                                                <span style={{ fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: 'rgba(4,75,53,0.08)', color: EDU.green, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI-readable</span>
                                            ) : (
                                                <span style={{ fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: 'rgba(245,158,11,0.12)', color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em' }}>No text — paste it below</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {f.file_url && (
                                        <a href={f.file_url} target="_blank" rel="noreferrer" style={{
                                            width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: EDU.muted, transition: 'all 0.2s',
                                        }} onMouseEnter={e => e.currentTarget.style.background = EDU.cream} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <ExternalLink size={16} />
                                        </a>
                                    )}
                                    <button
                                        id={`edu-delete-file-${f.id}`}
                                        onClick={() => handleDelete(f)}
                                        style={{
                                            width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#ef4444', cursor: 'pointer', transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* AI note */}
            <div style={{
                background: `${EDU.blue}08`, padding: 16, borderRadius: 16,
                border: `1px solid ${EDU.blue}15`, display: 'flex', gap: 12, alignItems: 'flex-start',
            }}>
                <CheckCircle size={18} color={EDU.blue} style={{ flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontSize: 12, color: EDU.slate, lineHeight: 1.5, margin: 0 }}>
                    {t('education.knowledgeUploader.aiNote')}
                </p>
            </div>

            {/* ── Inline delete confirm (replaces window.confirm) ── */}
            <AnimatePresence>
                {pendingDelete && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 200,
                            background: 'rgba(6,6,12,0.85)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                        }}
                        onClick={() => setPendingDelete(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 12 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.94, y: 12 }}
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#0e0e17', borderRadius: 20, padding: '28px 24px',
                                maxWidth: 360, width: '100%',
                                boxShadow: '0 24px 60px rgba(0,0,0,0.8)',
                                border: '1px solid rgba(255,255,255,0.10)',
                            }}
                        >
                            <p style={{ fontWeight: 800, color: EDU.navy, fontSize: 16, marginBottom: 8 }}>
                                {t('education.knowledgeUploader.deleteConfirmTitle', 'Delete file?')}
                            </p>
                            <p style={{ fontSize: 14, color: EDU.slate, marginBottom: 20, lineHeight: 1.5 }}>
                                {t('education.knowledgeUploader.deleteConfirm', { name: pendingDelete.file_name })}
                            </p>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    onClick={() => setPendingDelete(null)}
                                    style={{
                                        flex: 1, padding: '11px', borderRadius: 12,
                                        border: `1px solid ${EDU.border}`, background: EDU.cream,
                                        color: EDU.slate, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                    }}
                                >
                                    {t('education.common.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    style={{
                                        flex: 1, padding: '11px', borderRadius: 12,
                                        border: 'none', background: '#ef4444',
                                        color: '#ffffff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                    }}
                                >
                                    {t('education.common.delete', 'Delete')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}